import * as React from 'react';
import i18n from 'i18n';
import { AppError } from 'types';
import { SubmitResult } from './Form.interface';

/**
 * Counts the actual number of invalid fields, traversing nested field-array
 * structures (e.g. `{ ingredients: [{ searchTerm: '...' }, { searchTerm: '...' }] }`)
 * so the user-facing message reflects every offending field, not just the
 * top-level keys.
 */
export const countFieldErrors = (errors: unknown): number => {
  if (errors === null || errors === undefined) {
    return 0;
  }

  if (typeof errors === 'string') {
    return errors.length > 0 ? 1 : 0;
  }

  if (React.isValidElement(errors)) {
    return 1;
  }

  if (Array.isArray(errors)) {
    return errors.reduce<number>((total, item) => total + countFieldErrors(item), 0);
  }

  if (typeof errors === 'object') {
    return Object.values(errors).reduce<number>((total, item) => total + countFieldErrors(item), 0);
  }

  return 1;
};

export const handleFormErrors = <T extends {}>(result: SubmitResult<T>,
  mapErrorsToFields?: (receivedErrors: any) => AppError<T>) => {
  const { errors } = result.error ?? result;
  let formErrors: AppError<T>;
  let errorMessages: string[] = [];

  formErrors = errors;

  if (mapErrorsToFields) {
    formErrors = mapErrorsToFields(errors);
  }

  const { globalError, ...fieldErrors } = formErrors;

  // handles global form submission error
  if (globalError) {
    if (Array.isArray(globalError)) {
      errorMessages = errorMessages.concat(globalError);
    } else {
      errorMessages.push(globalError);
    }
  }

  const count = countFieldErrors(fieldErrors);

  if (count > 0) {
    // has field errors, prompts the user to fix them
    errorMessages.push(i18n.t('generic.formsValidation.fixFields', { count }));
  }

  return {
    formErrors,
    errorMessages,
  };
};
